import { useMemo } from 'react'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { PermissionString, Role } from './types'
import { PermissionResolver } from './PermissionResolver'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { SYSTEM_ROLES } from './systemRoles'
import { isDemoEnv } from 'lib/environment/environment'

export type CanFunctionOptions = {
  needsEveryPermission?: boolean
}

const CAN_OPTIONS_DEFAULTS: CanFunctionOptions = {
  needsEveryPermission: false
}

export type CanFunction = (
  permission: PermissionString | PermissionString[],
  options?: CanFunctionOptions
) => boolean

/**
 * This is inherently a bit limited, because when we return role information
 * from the API we don't associate permissions with roles. We can't, therefore,
 * say 'the permission you get from being a sales user in this context is not applicable'.
 * If we were to add a new role type that has access to quote editing, and should be
 * locked out in a similar manner to sales, then this would not cover that new scenario.
 *
 * Naturally, the real solution here is to have fine grained RBAC resources and a custom role,
 * so we can control this directly through RBAC resources! This is just a workaround to enable
 * a deal.
 *
 * @param roles
 * @returns
 */
export const userIsSalesButNotAdmin = (roles: Array<{ roleName: string }>) =>
  Boolean(
    roles.find(role => role.roleName === 'salesUser') &&
      !roles.find(role => role.roleName === 'admin')
  )

export const userCanEditQuotes = (roles: Array<Role>) => {
  return Boolean(
    roles.find(role =>
      role.permissions.some(
        p => p.resource === 'org.quote' && p.actions?.includes('update')
      )
    )
  )
}

export const applyQuotePsuedoPermissionsToRoles = (roles: Array<Role>) => {
  return roles.map(role => ({
    ...role,
    permissions: role.permissions.map(p => {
      if (p.resource === 'org.quote' && p.actions) {
        return {
          ...p,
          actions: [...p.actions, 'template.update', 'content.update']
        }
      }

      return p
    })
  }))
}

export function usePermissions() {
  const selfQuery = useQuery(dashboardv99990101Client.getSelf)
  const { useRestrictedQuoteEditingForSales } = useFeatureFlags()

  const self = selfQuery.data

  const roles = self?.roles ?? []
  /**
   * Applies the quote 'psuedo permissions', which are sub resources that are not
   * directly controlled by RBAC currently, but should be in the future!
   * https://linear.app/sequence-hq/issue/BADGE-4438/implement-rbac-functionality-to-enable-11x-cpq-requirements
   */
  const resolver = useMemo(() => {
    if (useRestrictedQuoteEditingForSales && userIsSalesButNotAdmin(roles)) {
      return new PermissionResolver(roles)
    }

    if (userCanEditQuotes(roles)) {
      return new PermissionResolver(applyQuotePsuedoPermissionsToRoles(roles))
    }

    return new PermissionResolver(roles)
  }, [roles, useRestrictedQuoteEditingForSales])

  const can: CanFunction = (permission, options = CAN_OPTIONS_DEFAULTS) => {
    if (isDemoEnv()) {
      return true
    }

    if (Array.isArray(permission)) {
      if (options.needsEveryPermission) {
        return permission.every(p => resolver.hasPermission(p))
      }
      return permission.some(p => resolver.hasPermission(p))
    }

    return resolver.hasPermission(permission)
  }

  return {
    can,
    roles,
    isAdmin: roles.some(role => role.roleName === SYSTEM_ROLES.admin),
    isLoading: selfQuery.isLoading,
    error: selfQuery.error
  }
}
